/*
 * Copyright (c) 2026 上海安能聚创供应链管理有限公司. All rights reserved.
 * 公司内部代码，未经书面授权，不得以任何形式复制、传播或用于与安能无关的其他用途.
 */
import { message } from 'antd';
import moment from 'moment';
import { all } from 'redux-saga/effects';
import { selectInOutVolumeTimeSlot, selectInOutVolumeTimeSlotPlan } from '@/services/api';
import { METRIC_GROUPS, PLAN_METRIC_GROUPS } from '../constants';

/**
 * 统计日期默认值（当日实时）：运营日 12:00 切日——12 点前默认昨日、12 点后默认当日（与切点自洽）
 */
export const defaultStatisticalDate = () => {
  const now = moment();
  return now.hour() < 12 ? now.clone().subtract(1, 'days') : now;
};

/**
 * 统计日期默认值（事后分析）：只回看已完结运营日——
 * 12 点后当日运营日已切新、上一运营日（昨日）刚完结；12 点前昨日运营日尚未走完，
 * 最近完结运营日为前日（运营日 (D-2 12:00, D-1 12:00]）
 */
export const defaultReviewStatisticalDate = () => {
  const now = moment();
  return now.hour() < 12 ? now.clone().subtract(2, 'days') : now.clone().subtract(1, 'days');
};

/**
 * 后端响应 VO 摊平为单行表格数据（指标组前缀 + 时段下标作为字段名）
 */
const buildRow = (vo, metricGroups) => {
  if (!vo) {
    return null;
  }
  const row = {
    key: `${vo.siteCode}-${vo.statisticalDate}`,
    statisticalDate: vo.statisticalDate,
    siteName: vo.siteName,
  };
  metricGroups.forEach((group) => {
    (vo[group.field] || []).forEach((slot, index) => {
      // 后端返回 kg，前端统一换算为吨（÷1000，四舍五入保留 1 位小数），图表与导出共用
      const slotValue = slot && slot.value !== null && slot.value !== undefined ? slot.value : 0;
      row[`${group.key}_${index}`] = Number((slotValue / 1000).toFixed(1));
    });
  });
  return row;
};

export default {
  namespace: 'inOutVolumeTimeSlot',
  state: {
    // 当前 tab：realtime=当日实时、review=事后分析
    activeKey: 'realtime',
    // ===== 当日实时 =====
    // 统计日期（moment 对象，必填）
    statisticalDate: defaultStatisticalDate(),
    // 分拨中心选中项（{ code, name }，必填）
    siteInfo: null,
    // 表格单行数据（查询成功前为 null）
    tableData: null,
    // ===== 事后分析 =====
    // 统计日期（moment 对象，必填，默认值规则与实时相反：12 点前=前日、12 点后=昨日）
    reviewStatisticalDate: defaultReviewStatisticalDate(),
    // 分拨中心选中项（{ code, name }，必填）
    reviewSiteInfo: null,
    // 图表 1 数据（实际进出货量，行结构同 tableData）
    reviewActualData: null,
    // 图表 2 数据（计划到达/出发货量）
    reviewPlanData: null,
  },
  effects: {
    *fetchData(_, { call, put, select }) {
      const { statisticalDate, siteInfo } = yield select(
        ({ inOutVolumeTimeSlot }) => inOutVolumeTimeSlot,
      );
      const response = yield call(selectInOutVolumeTimeSlot, {
        statisticalDate: statisticalDate && statisticalDate.format('YYYY-MM-DD'),
        siteCode: siteInfo && siteInfo.code,
        siteName: siteInfo && siteInfo.name,
      });
      if (response && response.data) {
        yield put({
          type: 'update',
          payload: { tableData: buildRow(response.data, METRIC_GROUPS) },
        });
      } else {
        yield put({ type: 'update', payload: { tableData: null } });
        message.warning((response && response.message) || '查询无数据');
      }
    },
    *fetchReviewData(_, { call, put, select }) {
      const { reviewStatisticalDate, reviewSiteInfo } = yield select(
        ({ inOutVolumeTimeSlot }) => inOutVolumeTimeSlot,
      );
      const params = {
        statisticalDate: reviewStatisticalDate && reviewStatisticalDate.format('YYYY-MM-DD'),
        siteCode: reviewSiteInfo && reviewSiteInfo.code,
        siteName: reviewSiteInfo && reviewSiteInfo.name,
      };
      // 两图表各自独立查询：图表 1 复用实际货量接口（支持历史运营日），图表 2 走计划接口
      const [actualResponse, planResponse] = yield all([
        call(selectInOutVolumeTimeSlot, params),
        call(selectInOutVolumeTimeSlotPlan, params),
      ]);
      const actualData =
        actualResponse && actualResponse.data ? buildRow(actualResponse.data, METRIC_GROUPS) : null;
      const planData =
        planResponse && planResponse.data ? buildRow(planResponse.data, PLAN_METRIC_GROUPS) : null;
      if (!actualData && !planData) {
        message.warning(
          (actualResponse && actualResponse.message) ||
            (planResponse && planResponse.message) ||
            '查询无数据',
        );
      }
      yield put({
        type: 'update',
        payload: { reviewActualData: actualData, reviewPlanData: planData },
      });
    },
  },
  reducers: {
    update(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};
